import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import MyWalletSidebar from '../MyWalletSidebar';
import { fetchCoinOrderDetail } from '../../services/coinPurchaseService';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';
import { GAMIFICATION_ROUTES } from '../gamification/gamificationRoutes';
import { clearExamCheckout } from './examDraft';
import { assessmentNavigation } from './assessmentNavigation';
import { paymentStatus } from './paymentStatus';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../gamification/Coinstore.css';
import '../../questionBank/QuestionBankList.css';
import './SelectPaymentMethod.css';
import './PaymentResult.css';

function pendingOrder() {
  try { return JSON.parse(sessionStorage.getItem('subscriptionPurchasePending') || 'null'); }
  catch { return null; }
}

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stored = pendingOrder();
  const search = new URLSearchParams(location.search || '');
  const orderId = search.get('vk_order_id') || location.state?.vkOrderId || stored?.vkOrderId;
  const matchingStored = stored?.vkOrderId === orderId ? stored : null;
  const purchaseType = search.get('kind') === 'exam' ? 'examSubscription' : location.state?.purchaseType || matchingStored?.purchaseType || 'coins';
  const purchaseContext = purchaseType === 'assessmentSubscription'
    ? assessmentNavigation({ ...matchingStored, ...location.state }) : {};
  const quizReturnTarget = purchaseContext.assessmentReturnTarget;
  const storePath = purchaseType === 'rapid' ? SUBSCRIPTION_ROUTES.rapidFireAttempts
    : purchaseType === 'assessment' ? SUBSCRIPTION_ROUTES.assessmentReattempts
    : purchaseType === 'subject' ? SUBSCRIPTION_ROUTES.subjectPlans
    : purchaseType === 'assessmentSubscription' ? SUBSCRIPTION_ROUTES.assessmentPlans
    : purchaseType === 'examSubscription' ? SUBSCRIPTION_ROUTES.examPlans
    : location.state?.storePath === GAMIFICATION_ROUTES.coinStore || matchingStored?.storePath === GAMIFICATION_ROUTES.coinStore
      ? GAMIFICATION_ROUTES.coinStore : SUBSCRIPTION_ROUTES.coinStore;
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const activeRequest = useRef(false);

  const refresh = useCallback(async () => {
    if (!orderId || activeRequest.current) return;
    activeRequest.current = true;
    setLoading(true);
    setError('');
    try {
      const next = await fetchCoinOrderDetail(orderId);
      setDetail(next);
      setStatus(paymentStatus(next.status, purchaseType));
      setAttempts((count) => count + 1);
    } catch (requestError) {
      setError(requestError.message || 'Unable to check payment status.');
      setAttempts((count) => count + 1);
    } finally {
      setLoading(false);
      activeRequest.current = false;
    }
  }, [orderId, purchaseType]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!orderId || status !== 'pending' || error || loading || attempts >= 20) return undefined;
    const timer = setTimeout(refresh, 3000);
    return () => clearTimeout(timer);
  }, [orderId, status, error, loading, attempts, refresh]);

  const returnToStore = async () => {
    if (status === 'success' || status === 'failed') sessionStorage.removeItem('subscriptionPurchasePending');
    if (purchaseType === 'examSubscription' && ['success', 'failed'].includes(status)) clearExamCheckout();
    if (purchaseType === 'rapid') {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['rapidFireReattempts'] }),
        queryClient.invalidateQueries({ queryKey: ['rapidFireBatchList'] }),
      ]);
    } else if (purchaseType === 'assessment') {
      await queryClient.invalidateQueries({ queryKey: ['quizReattempts'] });
    } else if (purchaseType === 'subject') {
      await queryClient.invalidateQueries({ queryKey: ['userSubjectSubscriptions'] });
    } else if (purchaseType === 'assessmentSubscription') {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userAssessmentSubscriptions'] }),
        queryClient.invalidateQueries({ queryKey: ['assessmentSubscriptions'] }),
        queryClient.invalidateQueries({ queryKey: ['attemptedQuizList'] }),
        queryClient.invalidateQueries({ queryKey: ['quizSeriesList'] }),
        queryClient.invalidateQueries({ queryKey: ['seriesQuizList'] }),
        queryClient.invalidateQueries({ queryKey: ['sidebarData'] }),
      ]);
    } else if (purchaseType === 'examSubscription') {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userExamSubscriptions'] }),
        queryClient.invalidateQueries({ queryKey: ['examSubscriptions'] }),
      ]);
    } else {
      await queryClient.invalidateQueries({ queryKey: ['gamificationCoinStore'] });
    }
    if (status === 'success') await queryClient.invalidateQueries({ queryKey: ['sidebarData'] });
    if (['subject', 'examSubscription'].includes(purchaseType)) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['podcastList'] }),
        queryClient.invalidateQueries({ queryKey: ['podcastDetail'] }),
        queryClient.invalidateQueries({ queryKey: ['infographicsList'] }),
        queryClient.invalidateQueries({ queryKey: ['infographicDetail'] }),
      ]);
    }
    if (quizReturnTarget) {
      navigate(quizReturnTarget.pathname, { replace: true, state: quizReturnTarget.state });
      return;
    }
    navigate(storePath, { replace: true, state: ['subject', 'assessmentSubscription', 'examSubscription'].includes(purchaseType) ? { ...purchaseContext, stateId: location.state?.stateId ?? matchingStored?.stateId, tab: 'mySubscription' } : undefined });
  };

  return (
    <>
    <style>{`.homeDashboard-wrapper .page-content { padding-right: 0; }
      .homeDashboard-wrapper .main-content { height: 100vh; overflow: auto; box-sizing: border-box; padding-bottom: 35px; min-height: 0; }`}</style>
    <HomeDashboardLayout showHeader={false}>
      <div className="qbl-page qbankList-page page-padding start-gamification coin-payment-page payment-result-page">
        <div className="start-quiz qbl-main instruction-main">
          <div className="hideProfile-page"><div className="categories-page-header mb-0">
            <div><button type="button" className="categories-back-button" onClick={returnToStore} aria-label="Go back"><i className="bi bi-chevron-left" /></button></div>
            <div><h4 className="d-flex categories-page-title"> </h4></div><div><HeaderProfileSection /></div>
          </div></div>
          <div className="coine-balance-wrapper">
            <div className="rapid-fire-header"><h1 className="qb-title">Payment <span>Status</span></h1></div>
            <section className={`payment-result-card payment-result-card--${status}`} aria-label="Payment result">
            <div className="payment-result-card__icon" aria-hidden="true"><i className={`bi ${status === 'success' ? 'bi-check-circle' : status === 'failed' ? 'bi-x-circle' : 'bi-clock'}`} /></div>
            {!orderId ? <p role="alert">No purchase order found.</p> : (
              <div className="payment-result-card__details">
                <h2 aria-live="polite">{loading && !detail ? 'Checking payment...' : status === 'success' ? 'Payment successful' : status === 'failed' ? 'Payment failed' : status === 'unknown' ? 'Checking payment status' : 'Payment pending'}</h2>
                <p>Order ID: <strong>{orderId}</strong></p>
                {detail?.amounts?.final_amount != null ? <p>Amount: ₹ {Number(detail.amounts.final_amount).toFixed(2)}</p> : null}
                {detail?.payment_gateway_name ? <p>Payment method: {detail.payment_gateway_name}</p> : null}
                {location.state?.paymentId ? <p>Payment ID: {location.state.paymentId}</p> : null}
                {status === 'pending' ? <p>We are waiting for confirmation from the payment server. Your {purchaseType === 'examSubscription' ? 'exam subscription' : purchaseType === 'subject' ? 'subject subscription' : purchaseType === 'assessmentSubscription' ? 'assessment subscription' : purchaseType === 'rapid' || purchaseType === 'assessment' ? 'remaining attempts' : 'coin balance'} will update after confirmation.</p> : null}
                {error ? <p role="alert">{error}</p> : null}
                {(error || status === 'unknown' || (status === 'pending' && attempts >= 20)) ? <button type="button" className="coinn-pack-buy-btn" onClick={refresh} disabled={loading}>Refresh status</button> : null}
              </div>
            )}
            <button type="button" className="coinn-pack-buy-btn" onClick={returnToStore}>Return to {quizReturnTarget ? 'Quiz' : purchaseType === 'examSubscription' ? 'My Exam Subscriptions' : purchaseType === 'subject' ? 'My Subject Subscriptions' : purchaseType === 'assessmentSubscription' ? 'My Assessment Subscriptions' : purchaseType === 'rapid' ? 'Rapid Attempts' : purchaseType === 'assessment' ? 'Assessment Attempts' : 'Coin Store'}</button>
            </section>
          </div>
        </div>
        <MyWalletSidebar withTopOffset={false} />
      </div>
    </HomeDashboardLayout>
    </>
  );
}
